import React from 'react';

import { isHeroPlannerScheduleDebugFailure } from '~/queries/customerCommunication/requestGetTodaysEscapesScheduleDebug';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

import { HERO_PLANNER_DEBUG_DEFAULT_LOYALTY_ID, HeroPlannerDebugRow } from '../../utils/heroPlannerDebugUtils';

import HeroPlannerDebugJsonSection from './HeroPlannerDebugJsonSection';
import HeroPlannerDebugStatusChip from './HeroPlannerDebugStatusChip';

interface Props {
  row?: HeroPlannerDebugRow;
  onClose: () => void;
}

function HeroPlannerDebugResponseDialog({ row, onClose }: Props) {
  const outcome = row?.outcome;
  const failure = isHeroPlannerScheduleDebugFailure(outcome) ? outcome : undefined;
  const responseBody = outcome?.ok ? { result: outcome.schedule } : failure?.body;

  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="hero-planner-debug-response-title">
      {row && (
        <>
          <DialogTitle id="hero-planner-debug-response-title">
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
              <span>
                {row.target.countryGroupName} › {row.target.countryName} › {row.target.stateName} ›{' '}
                {row.target.cityName} › {row.target.segmentName}
                {row.target.loyalty !== HERO_PLANNER_DEBUG_DEFAULT_LOYALTY_ID && ` › ${row.target.loyaltyName}`}
              </span>
              <HeroPlannerDebugStatusChip status={row.status} outcome={outcome} />
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            <Stack gap={2}>
              {outcome && (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Request · {outcome.durationMs}ms
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                    GET {outcome.url}
                  </Typography>
                </Box>
              )}
              {failure && (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Error
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    {failure.message}
                  </Typography>
                </Box>
              )}
              {row.heroRow && (
                <HeroPlannerDebugJsonSection
                  label={row.status === 'heroOnly' ? 'Hero planner row this segment falls back to' : 'Hero planner row'}
                  value={{ ...row.heroRow, sendDate: row.heroRow.sendDate?.format('YYYY-MM-DD') }}
                />
              )}
              <HeroPlannerDebugJsonSection label="Response body" value={responseBody} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="text" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default HeroPlannerDebugResponseDialog;
